<!--赖传峰-->
<!--2022/4/8-->
<template>
  <div
    ref="dom"
    class="restore"
  >
    <PPButton
      :disabled="state.loading && state.scene !== RestoreScene.UNKNOWN"
      :loading="state.loading && state.scene === RestoreScene.UNKNOWN"
      class="restore-btn"
      type="primary"
      @click="restoreWithTrack"
    >
      <div
        v-if="props.preview && shareStore.restoreResult.status === RestoreResultStatus.UNKNOWN"
        class="badge"
      >
        {{ $t('watch-instantly') }}
      </div>
      {{ state.btnTitle() }}
    </PPButton>
    <ClientOnly>
      <GuideAppPopup
        ref="guideAppPopupRef"
        @close="restore"
        @to-app="handleToApp"
      />
      <OpenPcDialog ref="openAppDialogRef" />
    </ClientOnly>
  </div>
</template>
<script lang="ts" setup>
import type { CookieOptions } from '#app'
import {
  appendGlobalExtData,
  inject,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  useCookie,
  useI18n,
  useNuxtApp,
  useRoute,
  watch,
} from '#imports'
import { getTask, restoreShare, type RestoreShareBody } from '@/api'
import PPButton from '@/components/PPButton.vue'
import { openAndHighlightFileInDriveLink, shareTrackParams } from '@/components/share/share.script'
import useMessage from '@/composables/useMessage'
import useShareId from '@/composables/useShareId'
import { useLayout } from '@/store/layout'
import { RestoreResultStatus, useShare } from '@/store/share'
import { useUserInfo } from '@/store/userInfo'
import $track, { type ShareSaveFrom } from '@/track/webShare'
import {
  CustomEventType,
  InjectionKey,
  PhaseType,
  RestoreScene,
  RestoreStatus,
  ServerError,
  ShareStatus,
  StorageKey,
} from '@/types/enum'
import { authByLocalStorage } from '@/utils'
import toApp from '@/utils/toApp'
import useShareFiles from '~/composables/useShareFiles'
import { getUserIdFromCredentials } from '~/utils/getUserIdFromCredentials'
import { useWebDecompression } from './fileExplorer.script'
import GuideAppPopup from './GuideAppPopup.vue'
import OpenPcDialog from './OpenPcDialog.vue'

const props = defineProps({
  preview: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const { $getPasscodeToken } = useNuxtApp()
const { t } = useI18n()
const shareStore = useShare()
const route = useRoute()
const shareId = useShareId()
const isMobile = useLayout().isMobile
const showLoginDialog = inject<ShowLoginDialog>(InjectionKey.SHOW_LOGIN_DIALOG)
const dom = ref<HTMLElement>()
const goToDriveTimeout: { timeout?: ReturnType<typeof setTimeout>; callback?: VoidFunction } = {
  timeout: undefined,
  callback: undefined,
}
const userInfo = useUserInfo()
const { show: showPaymentPane } = useWebDecompression()

const state = reactive<{
  loading: boolean
  btnTitle(): string // 不使用回调的话，文案不跟随多语言
  scene: RestoreScene
  status: RestoreResultStatus
}>({
  loading: false,
  btnTitle: () => t('save-to-my-pikpak'),
  scene: RestoreScene.UNKNOWN,
  status: RestoreResultStatus.UNKNOWN,
})

const guideAppPopupRef = ref<typeof GuideAppPopup>()
const openAppDialogRef = ref<InstanceType<typeof OpenPcDialog>>()
const messageToastRef = ref<ReturnType<typeof useMessage>>()

/* 生成通用 body */
const restBody = (previewFileId?: string | null): Pick<RestoreShareBody, 'file_ids' | 'ancestor_ids' | 'params'> => {
  if (props.preview) {
    return previewFileId
      ? {
          // 不传 file_ids, 表示转存全部文件
          // file_ids: [previewFileId],
          params: { trace_file_ids: previewFileId },
        }
      : {}
  }

  const selectedIds = shareStore.selectedIds
  if (!selectedIds.length) return { params: { trace_file_ids: shareStore.data.files.map(el => el.id).join(',') } }

  const parentIds = route.params.parent_ids
  return {
    file_ids: selectedIds,
    params: { trace_file_ids: selectedIds.join(',') },
    ancestor_ids: Array.isArray(parentIds) ? parentIds : undefined,
  }
}

/* 重置状态 */
const resetRestoreState = () => {
  shareStore.restoreResult = {
    status: RestoreResultStatus.UNKNOWN,
    parentId: undefined,
    fileIds: undefined,
    message: undefined,
  }

  shareStore.restoreCache = {
    parentId: undefined,
    fileIds: undefined,
    savedIds: undefined,
    barMessage: undefined,
  }

  Object.assign(state, {
    loading: false,
    btnTitle: () => t('save-to-my-pikpak'),
  })

  Object.assign(goToDriveTimeout, {
    timeout: undefined,
    callback: undefined,
  })
}

const files = useShareFiles()
const recoverStatus = () => {
  const saveAllLabel = () => t('save-to-my-pikpak')
  const saveSelectsLabel = () => t('save-selects-to-my-pikpak')

  const savedIds = shareStore.restoreCache.savedIds || []
  const allSaved = savedIds.length && files.value.every(f => savedIds.includes(f.id))

  if (shareStore.selectedIds && shareStore.selectedIds.length && !props.preview) {
    const allPartSaved = shareStore.selectedIds.every(id => savedIds.includes(id))

    if (allPartSaved) {
      Object.assign(state, {
        btnTitle: () => t('partial-saved-button'),
        status: RestoreResultStatus.SUCCEED,
      })
    } else {
      Object.assign(state, {
        btnTitle: saveSelectsLabel,
        status: RestoreResultStatus.UNKNOWN,
      })
    }
  } else if (allSaved) {
    Object.assign(state, {
      btnTitle: () => t('go-to-drive-view-files'),
      status: RestoreResultStatus.SUCCEED,
    })
  } else {
    Object.assign(state, {
      btnTitle: saveAllLabel,
      status: RestoreResultStatus.UNKNOWN,
    })
  }
}

/**
 * 轮询分享保存状态，确保前往播放的时候能有文件数据
 * 1000ms 间隔
 */
const pollingTraceFileIds = async (taskId: string): Promise<string | undefined> => {
  let traceFileIds: string | undefined

  while (!traceFileIds) {
    const task: Task | null = await new Promise(resolve => {
      setTimeout(async () => resolve((await getTask(taskId)).data.value), 1000)
    })
    // 转存过程中发生错误，空间不足
    const error = task?.params?.error_detail
    const taskStatusError = task?.phase === PhaseType.ERROR
    // 转存任务为失败状态时
    if (error || taskStatusError) {
      resetRestoreState()
      if (error === ServerError.FILE_SPACE_NOT_ENOUGH) {
        shareStore.fileSpaceNotEnoughVisible = true
        break
      } else {
        task?.message &&
          useMessage({
            type: 'error',
            duration: 5000,
            message: task?.message,
          })
      }
      break
    }

    traceFileIds = task?.params?.trace_file_ids
  }
  return traceFileIds
}

const handlePreviewCallback = (fileId?: string) => {
  // popup - 打开客户端弹窗
  // popup-mac - 无动作
  // 默认 - 跳转到云盘高亮 | 跳转到云盘并打开文件
  if (isMobile && !props.preview) {
    shareStore.restoreResult = {
      status: RestoreResultStatus.SUCCEED,
      parentId: shareStore.restoreCache.parentId,
      fileIds: shareStore.restoreCache.fileIds,
    }
  } else if ([RestoreScene.POPUP, RestoreScene.DETECT_DIALOG].includes(state.scene)) {
    openAppDialogRef.value?.show()
  } else if ([RestoreScene.POPUP_MAC, RestoreScene.DETECT_DIALOG_MAC].includes(state.scene)) {
    //
  } else if (props.preview || useCookie(StorageKey.RESTORE_TO_DRIVE)) {
    handlePreviewCallback.openFileInDrive(fileId)
  } else {
    shareStore.gotoHighlightFile()
  }
}
handlePreviewCallback.openFileInDrive = (_fileId?: string) => {
  const fileId = _fileId || shareStore.restoreCache?.fileIds?.[0] || ''
  window.dispatchEvent(new CustomEvent(CustomEventType.WILL_JUMP_TO_WEB))
  window.location.href = openAndHighlightFileInDriveLink(fileId)
}

/**
 * ancestor_ids 说明
 * 1. 转存全部文件，不需要传入file_ids，也不需要传入ancestor_ids
 * 2. 转存第一层的文件，例如A+B，file_ids传A、B，不需要传入ancestor_ids
 * 3. 转存第二层的文件，例如A.1, file_ids传A.1，ancestor_ids传A
 * 4. 转存第三层的文件，例如A1.1.1,  file_ids传A.1.1，ancestor_ids传A、A.1
 */
const restore = async (storageBody?: RestoreShareBody) => {
  messageToastRef.value?.close?.()

  if (state.status === RestoreResultStatus.SUCCEED) return handlePreviewCallback()

  const trackFrom: ShareSaveFrom = props.preview ? 'play_page' : 'share_page'

  // region 生成请求 body
  const previewFileId = sessionStorage.getItem(StorageKey.PREVIEW_FILE_ID)
  const body: RestoreShareBody = storageBody || {
    share_id: shareId.value,
    pass_code_token: $getPasscodeToken().value,
    ...restBody(previewFileId),
  }
  // endregion

  // region 未登录的情况
  const restoreAfterLogin = (bodyString: string) => {
    const cookieOptions: CookieOptions<string | null | undefined> & { readonly?: false | undefined } = {
      maxAge: 10 * 60, // 10分钟
      sameSite: 'lax',
      secure: true,
    }
    useCookie(StorageKey.RESTORE_CONTINUE, cookieOptions).value = 'true'
    props.preview && (useCookie(StorageKey.RESTORE_TO_DRIVE, cookieOptions).value = 'true')
    bodyString && localStorage.setItem(StorageKey.RESTORE_REQUEST_BODY, bodyString)
    showLoginDialog?.({
      afterLogin: restore,
      afterClose: () => {
        useCookie(StorageKey.RESTORE_CONTINUE).value = null
        useCookie(StorageKey.RESTORE_TO_DRIVE).value = null
      },
    })
  }

  // NOTICE 禁止用 await window.loginUI.login.isLoginStateValid() 代替，第三方登录成功后跳转，时机大概率比 window.loginUI 变量写入的更早，导致属性不存在
  if (!authByLocalStorage()) return restoreAfterLogin(JSON.stringify(body))

  // endregion 未登录的情况
  if (state.scene === RestoreScene.UNKNOWN) {
    Object.assign(state, {
      loading: true,
      btnTitle: () => t('restoring'),
    })
  } else {
    Object.assign(state, { loading: true })
  }

  // 尽可能的设置 user id
  appendGlobalExtData({ userid: getUserIdFromCredentials() || (await window.loginUI?.login.oAuth2Client.getSub()) })

  const { data, error } = await restoreShare(body)
  $track.web_share_save_request({
    ...shareTrackParams(true),
    from: trackFrom,
  })

  // region 接口返回错误，PC 以及 H5 预览使用 message，H5非预览去往错误页
  if (error.value) {
    $track.web_share_save_fail({
      ...shareTrackParams(true),
      from: trackFrom,
      error: error.value.error,
    })

    resetRestoreState() // 出现错误时，重置状态

    const errorCode = error.value.error
    const needJumpToVip =
      errorCode === ServerError.FILE_SPACE_NOT_ENOUGH || errorCode === ServerError.TASK_DAILY_CREATE_LIMIT

    // 非会员 & 播放器试看 & 保存失败
    if (!userInfo.premium && props.preview && needJumpToVip) {
      window.dispatchEvent(
        new CustomEvent(CustomEventType.JUMP_TO_VIP, {
          detail: errorCode,
        }),
      )
      return
    }

    if (errorCode === ServerError.UNAUTHENTICATED) return restoreAfterLogin(JSON.stringify(body))

    if (errorCode === ServerError.FILE_SPACE_NOT_ENOUGH) {
      shareStore.updatePremiumCallback = () => restore(storageBody)
      return (shareStore.fileSpaceNotEnoughVisible = true)
    }

    // 非会员，触发保存次数限制
    if (!userInfo.premium && errorCode === ServerError.TASK_DAILY_CREATE_LIMIT) {
      shareStore.updatePremiumCallback = () => restore(storageBody)
      const origin = window.location.origin
      showPaymentPane(
        `${origin}/drive/payment/join-vip?title=${encodeURIComponent(
          t('request-payment-limit-times', [3]),
        )}&initialIndex=2&referfrom=v_web_pikpak_hytq_number_limit&aidfrom=share_page`,
      )
      return
    }

    if (isMobile && !props.preview) {
      return (shareStore.restoreResult = {
        status: RestoreResultStatus.FAILED,
        message: error.value.message,
      })
    }

    useMessage({
      type: 'error',
      duration: 5000,
      message: error.value.message,
    })
  }
  // endregion

  const restoreStatus = data.value?.restore_status

  if (restoreStatus && restoreStatus !== RestoreStatus.RESTORE_UNKNOWN) {
    // region 异常的 share 状态同步至 share，触发页面变化
    if ([ShareStatus.PASS_CODE_EMPTY, ShareStatus.PASS_CODE_ERROR].includes(data.value.shareStatus)) {
      return (shareStore.data.share_status = ShareStatus.PASS_CODE_EMPTY)
    }
    // endregion

    const parentId = data.value.file_id
    const traceFileIds =
      restoreStatus === RestoreStatus.RESTORE_COMPLETE
        ? data.value.params?.trace_file_ids
        : await pollingTraceFileIds(data.value.restore_task_id)

    if (!traceFileIds) return

    const lastSaveIds = shareStore.restoreCache.savedIds || []
    const savedIds: string[] = body.file_ids ? body.file_ids : files.value.map(f => f.id)
    shareStore.restoreCache.savedIds = [...new Set([...lastSaveIds, ...savedIds])]
    shareStore.restoreCache.parentId = parentId

    $track.web_share_save_success({
      ...shareTrackParams(true),
      from: trackFrom,
    })

    // region 接口返回成功，预览的情况，都在原地，修改按钮文案，倒计时并前往云盘对应目录播放视频
    if (props.preview || useCookie(StorageKey.RESTORE_TO_DRIVE).value) {
      const fileId: string | undefined = await (async () => {
        try {
          return traceFileIds && previewFileId ? JSON.parse(traceFileIds)[previewFileId] : undefined
        } catch (e) {
          console.error(e)
          return undefined
        }
      })()

      fileId && (shareStore.restoreCache.fileIds = [fileId])

      handlePreviewCallback(fileId)

      state.loading = false
      return recoverStatus()
    }
    // endregion

    // region 接口返回成功，非预览的情况，H5 去结果页，PC 修改按钮展示
    const fieldIds: string[] | undefined = await (async () => {
      try {
        return traceFileIds ? Object.values(JSON.parse(traceFileIds)) : undefined
      } catch (e) {
        console.error(e)
        return undefined
      }
    })()

    shareStore.restoreCache.fileIds = fieldIds
    shareStore.restoreResult = {
      status: RestoreResultStatus.SUCCEED, // PC 的 RestoreResult.vue 不展示，所以借用 RestoreResultState
      parentId,
      fileIds: fieldIds,
    }

    state.loading = false

    return recoverStatus()
    // endregion
  }

  // 未在代码中特殊处理的转存失败与转存成功，都需要重置状态
  state.loading = false
  return recoverStatus()
}

const handleToApp = () => {
  const pwdCode = useCookie(`${StorageKey.SHARE_PWD_CODE}_${shareId.value}`).value || ''
  window.location.href = toApp.share(shareId.value, pwdCode)
}

const handleRestore = (scene?: RestoreScene) => {
  state.scene = scene || RestoreScene.UNKNOWN

  if (authByLocalStorage() || !isMobile) {
    restore()
  } else {
    state.loading = true
    // 尝试调端
    handleToApp()

    // 延迟出引导浮层
    setTimeout(() => {
      state.loading = false
      guideAppPopupRef.value?.show()
    }, 1000)
  }
}

/**
 * 数据上报需要区别于右键菜单保存的数据上报，所以包装一次
 */
const restoreWithTrack = () => {
  !props.preview &&
    $track.web_share_page_click({
      ...shareTrackParams(),
      button: shareStore.selectedIds.length ? 'save' : 'save_all',
    })

  props.preview &&
    $track.preview_end_floating_click({
      ...shareTrackParams(),
      button: 'save_all',
    })

  // pp-player 埋点
  props.preview && window.dispatchEvent(new CustomEvent(CustomEventType.RESTORE_CLICK))

  handleRestore()
}

shareStore.$onAction(({ name, after }) => name === 'setSelectedIds' && !state.loading && after(recoverStatus))

watch(
  () => route.params,
  () => !state.loading && recoverStatus(),
)
watch(
  () => shareStore.selectedIds,
  () => recoverStatus(),
)
onMounted(() => {
  if (useCookie(StorageKey.RESTORE_CONTINUE).value) {
    const storageQueryBody = localStorage.getItem(StorageKey.RESTORE_REQUEST_BODY)
    if (!storageQueryBody) return
    try {
      const body: RestoreShareBody = JSON.parse(storageQueryBody)
      useCookie(StorageKey.RESTORE_CONTINUE).value = null
      localStorage.removeItem(StorageKey.RESTORE_REQUEST_BODY)
      restore(body || undefined)
    } catch (e) {
      console.error(e)
    }
  }

  recoverStatus()

  window.addEventListener(CustomEventType.LOGOUT_SUCCESS, resetRestoreState)
})

onBeforeUnmount(() => {
  window.removeEventListener(CustomEventType.LOGOUT_SUCCESS, resetRestoreState)
})

// 广播保存状态
watch(state, newState => {
  if (props.preview) {
    const detail = { ...newState }
    const event = new CustomEvent(CustomEventType.RESTORE_STATUS, {
      detail,
    })
    window.dispatchEvent(event)
  }
})

defineExpose({
  click: handleRestore,
  dom: () => dom.value,
})
</script>
<style lang="less" scoped>
@import '@/assets/styles/util.less';

.restore,
.restore-btn {
  width: 100%;
  position: relative;
  --el-border-radius-base: 6px;
}

.badge {
  position: absolute;
  top: -8px;
  right: -1px;
  line-height: 20px;
  padding: 0 7px;
  font-size: 12px;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  border-bottom-left-radius: 20px;
  background: #d5e2ff;
  color: var(--color-primary);
}

@media screen and (min-width: @mobile-break-width) {
  .restore-btn {
    width: auto;
    padding: 0 24px;
    --el-border-radius-base: 6px;
  }

  .badge {
    top: -13px;
  }
}

@media screen and (max-width: @mobile-break-width) {
  .restore-btn {
    font-size: 17px;
  }
}
</style>
